// ============================================================
// WhatsApp quote delivery — sends every instant quote request
// to the IZHUB business WhatsApp automatically via Twilio
// (connected through the Lovable connector gateway).
// Called from the instant quote form after the email send
// succeeds. Failures are logged and never block the customer.
// ============================================================

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { SITE } from "@/config/site";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/twilio";

const quoteInput = z.object({
  name: z.string().max(120),
  phone: z.string().max(40),
  email: z.string().max(200),
  postcode: z.string().max(20),
  service: z.string().max(120),
  details: z.array(z.tuple([z.string().max(60), z.string().max(500)])).max(20),
  estimate: z.string().max(200),
  notes: z.string().max(1000).optional(),
});

export const sendQuoteToWhatsApp = createServerFn({ method: "POST" })
  .inputValidator((data) => quoteInput.parse(data))
  .handler(async ({ data }) => {
    const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
    const TWILIO_API_KEY = process.env.TWILIO_API_KEY;
    if (!LOVABLE_API_KEY || !TWILIO_API_KEY || !SITE.whatsappSenderNumber) {
      console.warn("[whatsapp-quote] Not configured (no gateway credentials or no WhatsApp sender number) — skipping WhatsApp delivery");
      return { ok: false, reason: "not-configured" as const };
    }

    const lines = [
      "*New instant quote request — IZHUB website*",
      "",
      `*Name:* ${data.name}`,
      `*Phone:* ${data.phone}`,
      `*Email:* ${data.email}`,
      `*Postcode:* ${data.postcode}`,
      `*Service:* ${data.service}`,
      ...data.details.map(([k, v]) => `*${k}:* ${v}`),
      `*Estimate:* ${data.estimate}`,
    ];
    if (data.notes) lines.push(`*Notes:* ${data.notes}`);
    const body = lines.join("\n");

    const sender = SITE.whatsappSenderNumber;
    const params = new URLSearchParams({
      To: `whatsapp:${SITE.whatsappBusinessNumber}`,
      From: `whatsapp:${sender}`,
      Body: body,
    });
    // Business-initiated WhatsApp messages outside a 24h customer window
    // require an approved template. Once the Twilio template is approved
    // (Content SID in site config), it is sent with the summary as variables.
    const contentSid = SITE.whatsappTemplateContentSid;
    if (contentSid) {
      params.set("ContentSid", contentSid);
      params.set(
        "ContentVariables",
        JSON.stringify({
          1: data.name,
          2: data.phone,
          3: data.service,
          4: body,
        }),
      );
    }

    try {
      const res = await fetch(`${GATEWAY_URL}/Messages.json`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${LOVABLE_API_KEY}`,
          "X-Connection-Api-Key": TWILIO_API_KEY,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });
      const text = await res.text();
      if (!res.ok) {
        console.error(`[whatsapp-quote] Twilio request failed [${res.status}]: ${text}`);
        return { ok: false, reason: "send-failed" as const };
      }
      console.log("[whatsapp-quote] Quote delivered to WhatsApp:", text);
      return { ok: true as const };
    } catch (err) {
      console.error("[whatsapp-quote] Request error", err);
      return { ok: false, reason: "request-error" as const };
    }
  });
